
  import { defineComponent } from 'vue';
  import { mapActions, mapGetters } from 'vuex';

  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import { TFilterDate, TFilterSelect } from '@/common/components/redesigned/Filter/types/filter';
  import PageTitle from '@/common/components/redesigned/PageTitle.vue';
  import TableWithOuterFilter from '@/common/components/redesigned/Table/TableWithOuterFilter.vue';
  import type { TTableWithOuterFilterConfig } from '@/common/components/redesigned/Table/types/tableTypes';
  import { TableEventTypes } from '@/common/components/redesigned/Table/types/tableTypes';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';

  import keyCountryIndicatorApi from '../api/keyCountryIndicator';
  import { ROUTE_NAMES } from '../constants/router';
  import {
    KEY_COUNTRY_INDICATOR_VES_ACTIONS_NAME,
    KEY_COUNTRY_INDICATOR_VES_GETTERS_NAME,
    KEY_COUNTRY_INDICATOR_VES_MUTATIONS_NAME,
  } from '../store/keyCountryIndicator/name';
  import { TKeyCountryIndicatorTableFilter, TKeyCountryIndicatorTableItem } from '../types/keyCountryIndicator';

  import { DIRECTIVES_ACTIONS_NAME, DIRECTIVES_GETTERS_NAME } from '@/store/directives';

  const PERMISSIONS = {
    view: Constants.Permissions.KeyCountryIndicatorView,
    create: Constants.Permissions.KeyCountryIndicatorCreate,
    update: Constants.Permissions.KeyCountryIndicatorUpdate,
    delete: Constants.Permissions.KeyCountryIndicatorDelete,
  };

  export default defineComponent({
    name: 'KeyCountryIndicator',
    components: {
      PageTitle,
      ButtonComponent,
      TableWithOuterFilter,
    },
    mixins: [security],
    data(): {
      tableConfig: TTableWithOuterFilterConfig<TKeyCountryIndicatorTableItem>;
      filter: Partial<TKeyCountryIndicatorTableFilter>;
    } {
      return {
        tableConfig: {
          columns: [
            {
              field: 'country',
              title: 'Страна',
              width: 161,
            },
            {
              field: 'indicator',
              title: 'Показатель',
              width: 161,
            },
            {
              field: 'year',
              title: 'Год',
              width: 85,
            },
            {
              field: 'firstSubjectRf',
              title: 'Субъект №1',
              width: 161,
            },
            {
              field: 'secondSubjectRf',
              title: 'Субъект №2',
              width: 161,
            },
            {
              field: 'thirdSubjectRf',
              title: 'Субъект №3',
              width: 161,
            },
            {
              field: 'fourthSubjectRf',
              title: 'Субъект №4',
              width: 161,
            },
            {
              field: 'fifthSubjectRf',
              title: 'Субъект №5',
              width: 161,
            },
          ],
          storePaths: {
            action: KEY_COUNTRY_INDICATOR_VES_ACTIONS_NAME.getCountriesList,
            mutation: KEY_COUNTRY_INDICATOR_VES_MUTATIONS_NAME.setCountriesList,
            getter: KEY_COUNTRY_INDICATOR_VES_GETTERS_NAME.getCountriesList,
          },
          eventConfigs: [],
        },
        filter: {},
      };
    },
    created() {
      this.checkPermissionAndRedirect(PERMISSIONS.view).then(async (isAllowed: boolean) => {
        if (!isAllowed) return;

        const [updateResponse, deleteResponse] = await Promise.all([
          this.checkPermission(PERMISSIONS.update),
          this.checkPermission(PERMISSIONS.delete),
        ]);

        const eventConfigs: TTableWithOuterFilterConfig<TKeyCountryIndicatorTableItem>['eventConfigs'] = [];

        if (updateResponse.data) {
          eventConfigs.push({
            type: TableEventTypes.EDIT,
            func: (item) => {
              this.$router.push({ name: ROUTE_NAMES.keyCountryIndicator.edit, params: { id: item.id } });
            },
          });
        }

        if (deleteResponse.data) {
          eventConfigs.push({
            type: TableEventTypes.DELETE,
            func: async (item, resetPagination) => {
              const result = await Constants.alert.fire(Constants.confirmOptions.delete);

              if (result.isConfirmed) {
                const response = await keyCountryIndicatorApi.delete(item.id);
                resetPagination();

                if (response.data.message)
                  Constants.alert.fire('Удаление', response.data.message, response.data.success ? 'success' : 'error');
              }
            },
          });
        }

        this.tableConfig.eventConfigs = eventConfigs;

        this.getDirectives();
      });
    },
    computed: {
      permissions: () => PERMISSIONS,
      ...mapGetters({
        partnerCountryList: DIRECTIVES_GETTERS_NAME.getPartnerCountrySearchIdList,
        regionsList: DIRECTIVES_GETTERS_NAME.getRegionsSearchIdList,
        indicatorsList: KEY_COUNTRY_INDICATOR_VES_GETTERS_NAME.getIndicators,
      }),
      getFiltersConfig(): [
        TFilterSelect<{ id: number; name: string }>,
        TFilterSelect<{ value: number; name: string }>,
        TFilterDate,
        TFilterSelect<{ id: number; name: string }>,
        TFilterSelect<{ id: number; name: string }>,
        TFilterSelect<{ id: number; name: string }>,
        TFilterSelect<{ id: number; name: string }>,
        TFilterSelect<{ id: number; name: string }>,
      ] {
        return [
          {
            type: 'select',
            label: 'Страна',
            placeholder: 'Все',
            options: this.partnerCountryList,
            valueProp: 'id',
            labelProp: 'name',
            vModel: 'countryId',
          },
          {
            type: 'select',
            label: 'Показатель',
            placeholder: 'Все',
            options: this.indicatorsList,
            valueProp: 'value',
            labelProp: 'name',
            vModel: 'indicatorId',
          },
          {
            type: 'date',
            mode: 'year',
            label: 'Год',
            vModel: 'year',
          },
          {
            type: 'select',
            label: 'Субъект №1',
            placeholder: 'Все',
            options: this.regionsList,
            valueProp: 'id',
            labelProp: 'name',
            vModel: 'firstSubjectRfId',
          },
          {
            type: 'select',
            label: 'Субъект №2',
            placeholder: 'Все',
            options: this.regionsList,
            valueProp: 'id',
            labelProp: 'name',
            vModel: 'secondSubjectRfId',
          },
          {
            type: 'select',
            label: 'Субъект №3',
            placeholder: 'Все',
            options: this.regionsList,
            valueProp: 'id',
            labelProp: 'name',
            vModel: 'thirdSubjectRfId',
          },
          {
            type: 'select',
            label: 'Субъект №4',
            placeholder: 'Все',
            options: this.regionsList,
            valueProp: 'id',
            labelProp: 'name',
            vModel: 'fourthSubjectRfId',
          },
          {
            type: 'select',
            label: 'Субъект №5',
            placeholder: 'Все',
            options: this.regionsList,
            valueProp: 'id',
            labelProp: 'name',
            vModel: 'fifthSubjectRfId',
          },
        ];
      },
    },
    methods: {
      ...mapActions({
        getPartnerCountrySearchId: DIRECTIVES_ACTIONS_NAME.getPartnerCountrySearchId,
        getRegionsSearchId: DIRECTIVES_ACTIONS_NAME.getRegionsSearchId,
        getIndicators: KEY_COUNTRY_INDICATOR_VES_ACTIONS_NAME.getIndicators,
      }),

      searchFilter(data: Partial<TKeyCountryIndicatorTableFilter>) {
        this.filter = data;
      },

      async getDirectives() {
        this.getPartnerCountrySearchId();
        this.getRegionsSearchId();
        this.getIndicators();
      },

      redirectToAddPage() {
        this.$router.push({ name: ROUTE_NAMES.keyCountryIndicator.add });
      },
    },
  });
